import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  VideoCamera, 
  Images, 
  DownloadSimple, 
  MegaphoneSimple, 
  Gear, 
  ChartBar, 
  SignOut, 
  Plus, 
  Trash, 
  PencilSimple,
  X,
  Upload,
  House,
  Pulse,
  ShieldCheck,
  Clock
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [videos, setVideos] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [settings, setSettings] = useState({});
  const [stats, setStats] = useState({ total_videos: 0, total_views: 0, subscriber_count: 0 });
  const [analytics, setAnalytics] = useState({ visitor_count: 0, page_views: 0 });
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const config = { withCredentials: true };
      const [overviewRes, videosRes, galleryRes, downloadsRes, announcementsRes, settingsRes, statsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard-overview`, config),
        axios.get(`${API}/videos`, config),
        axios.get(`${API}/gallery`, config),
        axios.get(`${API}/downloads`, config),
        axios.get(`${API}/announcements`, config),
        axios.get(`${API}/settings`, config),
        axios.get(`${API}/stats`, config),
        axios.get(`${API}/admin/analytics`, config)
      ]);

      setOverview(overviewRes.data);
      setVideos(videosRes.data);
      setGallery(galleryRes.data);
      setDownloads(downloadsRes.data);
      setAnnouncements(announcementsRes.data);
      setSettings(settingsRes.data);
      setStats(statsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const handleUploadFile = async (file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/upload`, formData, { withCredentials: true });
      toast.success('File uploaded successfully');
      return `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleCreateVideo = async (videoData) => {
    try {
      await axios.post(`${API}/admin/videos`, videoData, { withCredentials: true });
      toast.success('Video added successfully');
      fetchData();
      setShowVideoModal(false);
    } catch (error) {
      toast.error('Failed to add video');
    }
  };

  const handleUpdateVideo = async (videoId, videoData) => {
    try {
      await axios.put(`${API}/admin/videos/${videoId}`, videoData, { withCredentials: true });
      toast.success('Video updated successfully');
      fetchData();
      setShowVideoModal(false);
      setEditingVideo(null);
    } catch (error) {
      toast.error('Failed to update video');
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;
    try {
      await axios.delete(`${API}/admin/videos/${videoId}`, { withCredentials: true });
      toast.success('Video deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  const handleCreateGallery = async (file, title, description) => {
    const imageUrl = await handleUploadFile(file);
    if (!imageUrl) return;

    try {
      await axios.post(`${API}/admin/gallery`, { title, image_url: imageUrl, description }, { withCredentials: true });
      toast.success('Gallery item added');
      fetchData();
    } catch (error) {
      toast.error('Failed to add gallery item');
    }
  };

  const handleDeleteGallery = async (galleryId) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await axios.delete(`${API}/admin/gallery/${galleryId}`, { withCredentials: true });
      toast.success('Gallery item deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete gallery item');
    }
  };

  const handleCreateDownload = async (file, title, description) => {
    const fileUrl = await handleUploadFile(file);
    if (!fileUrl) return;

    try {
      await axios.post(`${API}/admin/downloads`, { title, description, file_url: fileUrl }, { withCredentials: true });
      toast.success('Download added');
      fetchData();
    } catch (error) {
      toast.error('Failed to add download');
    }
  };

  const handleDeleteDownload = async (downloadId) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await axios.delete(`${API}/admin/downloads/${downloadId}`, { withCredentials: true });
      toast.success('Download deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete download');
    }
  };

  const handleUpdateSettings = async (newSettings) => {
    try {
      await axios.put(`${API}/admin/settings`, newSettings, { withCredentials: true });
      toast.success('Settings updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const handleUpdateStats = async (newStats) => {
    try {
      await axios.put(`${API}/admin/stats`, newStats, { withCredentials: true });
      toast.success('Stats updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update stats');
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: House },
    { id: 'videos', label: 'Videos', icon: VideoCamera },
    { id: 'gallery', label: 'Gallery', icon: Images },
    { id: 'downloads', label: 'Downloads', icon: DownloadSimple },
    { id: 'announcements', label: 'Announcements', icon: MegaphoneSimple },
    { id: 'settings', label: 'Settings', icon: Gear },
    { id: 'analytics', label: 'Analytics', icon: ChartBar },
    { id: 'audit', label: 'Audit Logs', icon: ShieldCheck }
  ];

  return (
    <div className="min-h-screen bg-[#050810]">
      {/* Header */}
      <div data-testid="admin-header" className="bg-[#0F1528]/60 backdrop-blur-xl border-b border-cyan-500/20">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black font-['Unbounded'] text-white hero-glow">Admin Dashboard</h1>
            <p className="text-sm text-slate-400">Welcome back, {user?.name}</p>
          </div>
          <Button
            data-testid="logout-button"
            onClick={handleLogout}
            variant="outline"
            className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
          >
            <SignOut size={20} weight="duotone" className="mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="flex gap-2 overflow-x-auto pb-4">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                data-testid={`tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-cyan-500 text-black'
                    : 'bg-[#0F1528]/60 text-slate-300 hover:bg-[#0F1528]'
                }`}
              >
                <Icon size={20} weight="duotone" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="mt-6">
          {activeTab === 'overview' && <OverviewTab overview={overview} />}
          {activeTab === 'videos' && <VideosTab videos={videos} onDelete={handleDeleteVideo} onCreate={() => setShowVideoModal(true)} onEdit={(video) => { setEditingVideo(video); setShowVideoModal(true); }} />}
          {activeTab === 'gallery' && <GalleryTab gallery={gallery} onDelete={handleDeleteGallery} onCreate={handleCreateGallery} uploading={uploading} />}
          {activeTab === 'downloads' && <DownloadsTab downloads={downloads} onDelete={handleDeleteDownload} onCreate={handleCreateDownload} uploading={uploading} />}
          {activeTab === 'announcements' && <AnnouncementsTab announcements={announcements} fetchData={fetchData} />}
          {activeTab === 'settings' && <SettingsTab settings={settings} stats={stats} onUpdateSettings={handleUpdateSettings} onUpdateStats={handleUpdateStats} />}
          {activeTab === 'analytics' && <AnalyticsTab analytics={analytics} />}
          {activeTab === 'audit' && <AuditTab />}
        </div>
      </div>

      {/* Video Modal */}
      {showVideoModal && (
        <VideoModal
          video={editingVideo}
          onClose={() => { setShowVideoModal(false); setEditingVideo(null); }}
          onCreate={handleCreateVideo}
          onUpdate={handleUpdateVideo}
          onUpload={handleUploadFile}
          uploading={uploading}
        />
      )}
    </div>
  );
}

function VideosTab({ videos, onDelete, onCreate, onEdit }) {
  return (
    <div data-testid="videos-tab">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-white">Manage Videos</h2>
        <Button data-testid="add-video-button" onClick={onCreate} className="bg-cyan-500 hover:bg-cyan-400 text-black">
          <Plus size={20} weight="bold" className="mr-2" />
          Add Video
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {videos.map((video) => (
          <div key={video.id} data-testid={`admin-video-${video.id}`} className="card-glass rounded-lg p-4">
            <img src={video.thumbnail_url || 'https://via.placeholder.com/300x200'} alt={video.title} className="w-full aspect-video object-cover rounded mb-3" />
            <h3 className="text-white font-bold mb-2 line-clamp-1">{video.title}</h3>
            <p className="text-sm text-slate-400 mb-3 line-clamp-2">{video.description}</p>
            <div className="flex gap-2 text-xs mb-3 flex-wrap">
              {video.is_featured && <span className="bg-cyan-500/20 text-cyan-400 px-2 py-1 rounded">Featured</span>}
              {video.is_trending && <span className="bg-blue-500/20 text-blue-400 px-2 py-1 rounded">Trending</span>}
              {video.is_video_of_week && <span className="bg-purple-500/20 text-purple-400 px-2 py-1 rounded">Video of Week</span>}
              {video.is_short && <span className="bg-pink-500/20 text-pink-400 px-2 py-1 rounded">Shorts</span>}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="flex-1 border-cyan-500/30" onClick={() => onEdit(video)}>
                <PencilSimple size={16} weight="duotone" className="mr-1" /> Edit
              </Button>
              <Button size="sm" variant="outline" className="border-red-500/30 text-red-400" onClick={() => onDelete(video.id)}>
                <Trash size={16} weight="duotone" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GalleryTab({ gallery, onDelete, onCreate, uploading }) {
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please select an image');
      return;
    }
    await onCreate(file, title, description);
    setShowForm(false);
    setTitle('');
    setDescription('');
    setFile(null);
  };

  return (
    <div data-testid="gallery-tab">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-white">Manage Gallery</h2>
        <Button data-testid="add-gallery-button" onClick={() => setShowForm(true)} className="bg-cyan-500 hover:bg-cyan-400 text-black">
          <Plus size={20} weight="bold" className="mr-2" />
          Add Image
        </Button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="card-glass rounded-lg p-6 mb-6">
          <h3 className="text-lg font-bold text-white mb-4">Add Gallery Image</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-300 mb-2">Title</label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} required className="bg-[#0F1528] border-cyan-500/20" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-300 mb-2">Description (optional)</label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} className="bg-[#0F1528] border-cyan-500/20" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-300 mb-2">Image File</label>
              <Input type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])} required className="bg-[#0F1528] border-cyan-500/20" />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={uploading} className="bg-cyan-500 hover:bg-cyan-400 text-black">
                {uploading ? 'Uploading...' : 'Add Image'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="border-cyan-500/30">
                Cancel
              </Button>
            </div>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {gallery.map((item) => (
          <div key={item.id} data-testid={`admin-gallery-${item.id}`} className="card-glass rounded-lg p-4">
            <img src={item.image_url} alt={item.title} className="w-full aspect-video object-cover rounded mb-3" />
            <h3 className="text-white font-bold mb-2">{item.title}</h3>
            <Button size="sm" variant="outline" className="w-full border-red-500/30 text-red-400" onClick={() => onDelete(item.id)}>
              <Trash size={16} weight="duotone" className="mr-2" /> Delete
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function DownloadsTab({ downloads, onDelete, onCreate, uploading }) {
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please select a file');
      return;
    }
    await onCreate(file, title, description);
    setShowForm(false);
    setTitle('');
    setDescription('');
    setFile(null);
  };

  return (
    <div data-testid="downloads-tab">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-white">Manage Downloads</h2>
        <Button data-testid="add-download-button" onClick={() => setShowForm(true)} className="bg-cyan-500 hover:bg-cyan-400 text-black">
          <Plus size={20} weight="bold" className="mr-2" />
          Add Download
        </Button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="card-glass rounded-lg p-6 mb-6">
          <h3 className="text-lg font-bold text-white mb-4">Add Download</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-300 mb-2">Title</label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} required className="bg-[#0F1528] border-cyan-500/20" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-300 mb-2">Description</label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} required className="bg-[#0F1528] border-cyan-500/20" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-300 mb-2">File</label>
              <Input type="file" onChange={(e) => setFile(e.target.files[0])} required className="bg-[#0F1528] border-cyan-500/20" />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={uploading} className="bg-cyan-500 hover:bg-cyan-400 text-black">
                {uploading ? 'Uploading...' : 'Add Download'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="border-cyan-500/30">
                Cancel
              </Button>
            </div>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {downloads.map((item) => (
          <div key={item.id} data-testid={`admin-download-${item.id}`} className="card-glass rounded-lg p-4">
            <h3 className="text-white font-bold mb-2">{item.title}</h3>
            <p className="text-sm text-slate-400 mb-4">{item.description}</p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="flex-1 border-cyan-500/30" onClick={() => window.open(item.file_url, '_blank')}>
                <DownloadSimple size={16} weight="duotone" className="mr-1" /> Download
              </Button>
              <Button size="sm" variant="outline" className="border-red-500/30 text-red-400" onClick={() => onDelete(item.id)}>
                <Trash size={16} weight="duotone" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AnnouncementsTab({ announcements, fetchData }) {
  const [message, setMessage] = useState('');

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/announcements`, { message, is_active: true }, { withCredentials: true });
      toast.success('Announcement added');
      setMessage('');
      fetchData();
    } catch (error) {
      toast.error('Failed to add announcement');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/announcements/${id}`, { withCredentials: true });
      toast.success('Announcement deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  return (
    <div data-testid="announcements-tab">
      <h2 className="text-xl font-bold text-white mb-6">Manage Announcements</h2>
      <form onSubmit={handleCreate} className="card-glass rounded-lg p-6 mb-6">
        <h3 className="text-lg font-bold text-white mb-4">Add Announcement</h3>
        <div className="space-y-4">
          <Textarea
            data-testid="announcement-input"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Enter announcement message..."
            required
            className="bg-[#0F1528] border-cyan-500/20"
          />
          <Button type="submit" className="bg-cyan-500 hover:bg-cyan-400 text-black">
            <Plus size={20} weight="bold" className="mr-2" />
            Add Announcement
          </Button>
        </div>
      </form>

      <div className="space-y-4">
        {announcements.map((item) => (
          <div key={item.id} data-testid={`admin-announcement-${item.id}`} className="card-glass rounded-lg p-4 flex justify-between items-center">
            <p className="text-white">{item.message}</p>
            <Button size="sm" variant="outline" className="border-red-500/30 text-red-400" onClick={() => handleDelete(item.id)}>
              <Trash size={16} weight="duotone" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsTab({ settings, stats, onUpdateSettings, onUpdateStats }) {
  const [formSettings, setFormSettings] = useState(settings);
  const [formStats, setFormStats] = useState(stats);

  useEffect(() => {
    setFormSettings(settings);
    setFormStats(stats);
  }, [settings, stats]);

  const handleSettingsSubmit = (e) => {
    e.preventDefault();
    onUpdateSettings(formSettings);
  };

  const handleStatsSubmit = (e) => {
    e.preventDefault();
    onUpdateStats(formStats);
  };

  return (
    <div data-testid="settings-tab" className="space-y-6">
      <form onSubmit={handleSettingsSubmit} className="card-glass rounded-lg p-6">
        <h3 className="text-lg font-bold text-white mb-4">Site Settings</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-300 mb-2">YouTube URL</label>
            <Input
              data-testid="settings-youtube-input"
              value={formSettings.youtube_url || ''}
              onChange={(e) => setFormSettings({ ...formSettings, youtube_url: e.target.value })}
              placeholder="https://youtube.com/@slaynx"
              className="bg-[#0F1528] border-cyan-500/20"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-300 mb-2">Instagram URL</label>
            <Input
              data-testid="settings-instagram-input"
              value={formSettings.instagram_url || ''}
              onChange={(e) => setFormSettings({ ...formSettings, instagram_url: e.target.value })}
              placeholder="https://instagram.com/slaynx"
              className="bg-[#0F1528] border-cyan-500/20"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-300 mb-2">Contact Email</label>
            <Input
              data-testid="settings-email-input"
              value={formSettings.contact_email || ''}
              onChange={(e) => setFormSettings({ ...formSettings, contact_email: e.target.value })}
              placeholder="contact@slaynx.com"
              className="bg-[#0F1528] border-cyan-500/20"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-300 mb-2">About Text</label>
            <Textarea
              data-testid="settings-about-input"
              value={formSettings.about_text || ''}
              onChange={(e) => setFormSettings({ ...formSettings, about_text: e.target.value })}
              rows={4}
              placeholder="Tell visitors about yourself..."
              className="bg-[#0F1528] border-cyan-500/20"
            />
          </div>
          <Button type="submit" className="bg-cyan-500 hover:bg-cyan-400 text-black">
            Save Settings
          </Button>
        </div>
      </form>

      <form onSubmit={handleStatsSubmit} className="card-glass rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-white">Channel Stats</h3>
          <Button
            type="button"
            data-testid="sync-youtube-button"
            onClick={async () => {
              try {
                const { data } = await axios.post(`${API}/admin/sync-youtube`, {}, { withCredentials: true });
                toast.success(`Synced! Subs: ${data.stats.subscriber_count.toLocaleString()}, Views: ${data.stats.total_views.toLocaleString()}`);
                window.location.reload();
              } catch (err) {
                toast.error('Sync failed: ' + (err.response?.data?.detail || err.message));
              }
            }}
            className="bg-pink-500 hover:bg-pink-400 text-white text-xs"
          >
            🔄 Sync from YouTube
          </Button>
        </div>
        <p className="text-xs text-cyan-400/70 mb-4">Auto-syncs from YouTube every hour. Click sync for immediate refresh.</p>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-300 mb-2">Total Videos</label>
            <Input
              data-testid="stats-videos-input"
              type="number"
              value={formStats.total_videos || 0}
              onChange={(e) => setFormStats({ ...formStats, total_videos: parseInt(e.target.value) || 0 })}
              className="bg-[#0F1528] border-cyan-500/20"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-300 mb-2">Total Views</label>
            <Input
              data-testid="stats-views-input"
              type="number"
              value={formStats.total_views || 0}
              onChange={(e) => setFormStats({ ...formStats, total_views: parseInt(e.target.value) || 0 })}
              className="bg-[#0F1528] border-cyan-500/20"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-300 mb-2">Subscriber Count</label>
            <Input
              data-testid="stats-subscribers-input"
              type="number"
              value={formStats.subscriber_count || 0}
              onChange={(e) => setFormStats({ ...formStats, subscriber_count: parseInt(e.target.value) || 0 })}
              className="bg-[#0F1528] border-cyan-500/20"
            />
          </div>
          <Button type="submit" className="bg-cyan-500 hover:bg-cyan-400 text-black">
            Update Stats
          </Button>
        </div>
      </form>
    </div>
  );
}

function AnalyticsTab({ analytics }) {
  return (
    <div data-testid="analytics-tab">
      <h2 className="text-xl font-bold text-white mb-6">Analytics</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card-glass rounded-lg p-8 text-center">
          <div className="text-5xl font-black font-['Unbounded'] text-cyan-400 mb-2">{analytics.visitor_count}</div>
          <div className="text-slate-300 uppercase tracking-wide text-sm">Total Visitors</div>
        </div>
        <div className="card-glass rounded-lg p-8 text-center">
          <div className="text-5xl font-black font-['Unbounded'] text-cyan-400 mb-2">{analytics.page_views}</div>
          <div className="text-slate-300 uppercase tracking-wide text-sm">Page Views</div>
        </div>
      </div>
    </div>
  );
}

function OverviewTab({ overview }) {
  if (!overview) return <div data-testid="overview-tab" className="text-slate-400">Loading...</div>;
  const formatTime = (iso) => {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffMin < 1440) return `${Math.floor(diffMin / 60)}h ago`;
    return d.toLocaleString();
  };
  const actionColor = (action) => {
    if (action.includes('FAILED') || action.includes('BLOCKED') || action.includes('INVALID')) return 'text-red-400';
    if (action.includes('DELETED')) return 'text-orange-400';
    if (action.includes('CREATED') || action.includes('SUCCESS')) return 'text-green-400';
    if (action.includes('UPDATED') || action.includes('SENT')) return 'text-cyan-400';
    return 'text-slate-300';
  };

  const cards = [
    { label: 'Site Videos', value: overview.total_videos_on_site, color: 'from-cyan-500/20 to-cyan-500/5' },
    { label: 'YouTube Views', value: overview.youtube_stats.total_views, color: 'from-blue-500/20 to-blue-500/5', format: true },
    { label: 'Subscribers', value: overview.youtube_stats.subscriber_count, color: 'from-pink-500/20 to-pink-500/5', format: true },
    { label: 'Downloads', value: overview.total_downloads, color: 'from-purple-500/20 to-purple-500/5' },
    { label: 'Gallery Items', value: overview.total_gallery_items, color: 'from-indigo-500/20 to-indigo-500/5' },
    { label: 'Site Visitors', value: overview.site_analytics.visitor_count, color: 'from-emerald-500/20 to-emerald-500/5' }
  ];
  const fmt = (n) => n >= 1000 ? (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K' : n.toString();

  return (
    <div data-testid="overview-tab" className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {cards.map((c, i) => (
          <div key={i} className={`card-glass rounded-xl p-5 bg-gradient-to-br ${c.color}`}>
            <div className="text-3xl font-black font-['Unbounded'] text-white">{c.format ? fmt(c.value) : c.value}</div>
            <div className="text-xs uppercase tracking-wider text-slate-400 mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="card-glass rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Pulse size={20} weight="duotone" className="text-cyan-400" />
          <h2 className="text-lg font-bold text-white">Recent Activity</h2>
        </div>
        {overview.recent_activity.length === 0 ? (
          <p className="text-slate-400 text-sm">No activity yet</p>
        ) : (
          <div className="space-y-2">
            {overview.recent_activity.map((log) => (
              <div key={log.id} data-testid={`activity-${log.id}`} className="flex items-start gap-3 py-2 border-b border-cyan-500/5 last:border-0">
                <Clock size={14} weight="duotone" className="text-slate-500 mt-1 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className={`text-sm font-semibold ${actionColor(log.action)}`}>{log.action.replace(/_/g, ' ')}</span>
                    <span className="text-xs text-slate-500">{formatTime(log.timestamp)}</span>
                  </div>
                  {log.details && <p className="text-xs text-slate-400 truncate">{log.details}</p>}
                  <p className="text-[10px] text-slate-600">{log.user_email} · {log.ip}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AuditTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/audit-logs?limit=100`, { withCredentials: true })
      .then(r => setLogs(r.data))
      .catch(() => toast.error('Failed to load audit logs'))
      .finally(() => setLoading(false));
  }, []);

  const actionColor = (action) => {
    if (action.includes('FAILED') || action.includes('BLOCKED') || action.includes('INVALID')) return 'bg-red-500/20 text-red-400';
    if (action.includes('DELETED')) return 'bg-orange-500/20 text-orange-400';
    if (action.includes('CREATED') || action.includes('SUCCESS')) return 'bg-green-500/20 text-green-400';
    if (action.includes('UPDATED') || action.includes('SENT')) return 'bg-cyan-500/20 text-cyan-400';
    return 'bg-slate-500/20 text-slate-300';
  };

  return (
    <div data-testid="audit-tab">
      <h2 className="text-xl font-bold text-white mb-6">Audit Logs</h2>
      <div className="card-glass rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#0F1528] border-b border-cyan-500/10">
              <tr>
                <th className="text-left text-xs uppercase tracking-wider text-slate-400 px-4 py-3">Time</th>
                <th className="text-left text-xs uppercase tracking-wider text-slate-400 px-4 py-3">Action</th>
                <th className="text-left text-xs uppercase tracking-wider text-slate-400 px-4 py-3">User</th>
                <th className="text-left text-xs uppercase tracking-wider text-slate-400 px-4 py-3">IP</th>
                <th className="text-left text-xs uppercase tracking-wider text-slate-400 px-4 py-3">Details</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" className="px-4 py-8 text-center text-slate-400">Loading...</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan="5" className="px-4 py-8 text-center text-slate-400">No logs yet</td></tr>
              ) : logs.map((log) => (
                <tr key={log.id} className="border-b border-cyan-500/5 hover:bg-cyan-500/5">
                  <td className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap">{new Date(log.timestamp).toLocaleString()}</td>
                  <td className="px-4 py-3"><span className={`text-xs font-bold px-2 py-1 rounded ${actionColor(log.action)}`}>{log.action.replace(/_/g, ' ')}</span></td>
                  <td className="px-4 py-3 text-xs text-cyan-400">{log.user_email}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{log.ip}</td>
                  <td className="px-4 py-3 text-xs text-slate-300">{log.details || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function VideoModal({ video, onClose, onCreate, onUpdate, onUpload, uploading }) {
  const [formData, setFormData] = useState({
    title: video?.title || '',
    url: video?.url || '',
    description: video?.description || '',
    thumbnail_url: video?.thumbnail_url || '',
    is_featured: video?.is_featured || false,
    is_trending: video?.is_trending || false,
    is_video_of_week: video?.is_video_of_week || false,
    is_short: video?.is_short || false
  });

  const extractYouTubeId = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/|youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
      /^([a-zA-Z0-9_-]{11})$/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    const videoId = extractYouTubeId(url);
    const isShortUrl = url.includes('/shorts/');
    const newData = { ...formData, url };
    if (isShortUrl) newData.is_short = true;
    // Auto-fetch YouTube thumbnail (hqdefault works for both regular videos AND shorts reliably)
    if (videoId && (!formData.thumbnail_url || formData.thumbnail_url.includes('ytimg.com') || formData.thumbnail_url.includes('youtube.com/vi'))) {
      newData.thumbnail_url = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
    }
    setFormData(newData);
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = await onUpload(file);
    if (url) setFormData({ ...formData, thumbnail_url: url });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (video) {
      onUpdate(video.id, formData);
    } else {
      onCreate(formData);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="card-glass rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white">{video ? 'Edit Video' : 'Add Video'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X size={24} weight="bold" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-300 mb-2">Title</label>
            <Input
              data-testid="video-title-input"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              className="bg-[#0F1528] border-cyan-500/20"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-300 mb-2">YouTube URL</label>
            <Input
              data-testid="video-url-input"
              value={formData.url}
              onChange={handleUrlChange}
              required
              placeholder="https://youtube.com/watch?v=..."
              className="bg-[#0F1528] border-cyan-500/20"
            />
            <p className="text-xs text-cyan-400/70 mt-1">Thumbnail will be auto-fetched from YouTube</p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-300 mb-2">Description</label>
            <Textarea
              data-testid="video-description-input"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="bg-[#0F1528] border-cyan-500/20"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-300 mb-2">Thumbnail (auto-fetched from YouTube)</label>
            {formData.thumbnail_url && (
              <img 
                src={formData.thumbnail_url} 
                alt="Thumbnail" 
                className="w-full aspect-video object-cover rounded mb-2"
                onError={(e) => {
                  // Fallback to hqdefault if maxresdefault fails
                  if (e.target.src.includes('maxresdefault')) {
                    e.target.src = e.target.src.replace('maxresdefault', 'hqdefault');
                  }
                }}
              />
            )}
            <label className="text-xs text-slate-400 block mb-1">Override with custom thumbnail (optional):</label>
            <Input
              type="file"
              accept="image/*"
              onChange={handleThumbnailUpload}
              disabled={uploading}
              className="bg-[#0F1528] border-cyan-500/20"
            />
          </div>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-slate-300">
              <input
                data-testid="video-featured-checkbox"
                type="checkbox"
                checked={formData.is_featured}
                onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                className="w-4 h-4"
              />
              Featured Video
            </label>
            <label className="flex items-center gap-2 text-slate-300">
              <input
                data-testid="video-trending-checkbox"
                type="checkbox"
                checked={formData.is_trending}
                onChange={(e) => setFormData({ ...formData, is_trending: e.target.checked })}
                className="w-4 h-4"
              />
              Trending Video
            </label>
            <label className="flex items-center gap-2 text-slate-300">
              <input
                data-testid="video-week-checkbox"
                type="checkbox"
                checked={formData.is_video_of_week}
                onChange={(e) => setFormData({ ...formData, is_video_of_week: e.target.checked })}
                className="w-4 h-4"
              />
              Video of the Week
            </label>
            <label className="flex items-center gap-2 text-slate-300">
              <input
                data-testid="video-short-checkbox"
                type="checkbox"
                checked={formData.is_short}
                onChange={(e) => setFormData({ ...formData, is_short: e.target.checked })}
                className="w-4 h-4"
              />
              YouTube Shorts (vertical 9:16)
            </label>
          </div>
          <div className="flex gap-2 pt-4">
            <Button type="submit" disabled={uploading} className="flex-1 bg-cyan-500 hover:bg-cyan-400 text-black">
              {video ? 'Update Video' : 'Add Video'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose} className="border-cyan-500/30">
              Cancel
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
